import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { FIREBASE_CONFIG } from '../config/constants';
import { getInterviewCounts } from '../services/userService';

const Feedback = () => {
  const { user } = useAuth();
  const { sessionId } = useParams();
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interviewCounted, setInterviewCounted] = useState(false);

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!user || !sessionId) {
        console.log('No user or session ID found, skipping fetch');
        setLoading(false);
        return;
      }

      try {
        console.log('Starting feedback fetch for session:', sessionId);
        
        // Get the interview document to check if it's been counted
        const interviewRef = doc(db, FIREBASE_CONFIG.INTERVIEW_COLLECTION(), sessionId);
        const interviewDoc = await getDoc(interviewRef);
        
        if (interviewDoc.exists()) {
          const interviewData = interviewDoc.data();
          
          // Check if this interview has feedback and hasn't been counted yet
          if (interviewData.has_feedback === true && !interviewData.counted) {
            try {
              // Mark this interview as counted
              await updateDoc(interviewRef, {
                counted: true
              });
              
              // Get updated counts (this will reflect in UI automatically)
              await getInterviewCounts(user.uid);
              
              setInterviewCounted(true);
              console.log(`Interview ${sessionId} counted successfully`);
            } catch (countError) {
              console.error('Error updating interview count:', countError);
            }
          } else {
            setInterviewCounted(interviewData.counted || false);
            if (!interviewData.has_feedback) {
              console.log(`Interview ${sessionId} not counted - has_feedback is not true`);
            } else if (interviewData.counted) {
              console.log(`Interview ${sessionId} already counted previously`);
            }
          }
        }
        
        // Get the feedback document
        const feedbackRef = doc(
          db, 
          FIREBASE_CONFIG.INTERVIEW_COLLECTION(),
          sessionId,
          'feedback',
          'latest'
        );
        
        console.log('Fetching from path:', feedbackRef.path);
        const feedbackSnap = await getDoc(feedbackRef);
        
        if (feedbackSnap.exists()) {
          console.log('Feedback data:', feedbackSnap.data());
          setFeedback(feedbackSnap.data());
        } else {
          console.log('No feedback document found');
        }
      } catch (error) {
        console.error('Error fetching feedback:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [user, sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F17] text-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="min-h-screen bg-[#0B0F17] text-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl">No feedback available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F17] text-gray-100 py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-8">
          Interview Feedback
        </h1>
        
        <div className="bg-gray-900/30 backdrop-blur-sm rounded-2xl p-8 border border-gray-800/50">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-purple-400">
              Overall Score: {feedback.avg_score}/10
            </h2>
            <p className="text-xs text-gray-400 mt-2">
              Completed at: {feedback.completed_at}
            </p>
            {interviewCounted ? (
              <p className="text-xs text-green-400 mt-1">
                This interview has been counted toward your usage.
              </p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">
                This interview has been counted toward your usage.
              </p>
            )}
          </div>
          
          <div className="space-y-6">
            {Object.entries(feedback.feedback || {}).map(([topicNum, data]) => (
              <div key={topicNum} className="bg-gray-800/30 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-200 mb-4">
                  Topic {topicNum}
                </h3>
                <p className="text-gray-300 mb-4">{data.text}</p>
                <div className="text-blue-400">
                  Score: {data.score}/10
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback; 